import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const Icon = {
  events: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>,
  results: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1" fill="currentColor"/></svg>,
  org: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><path d="M3 21V7l7-4 7 4v14"/><path d="M3 21h18M9 21v-6h4v6"/></svg>,
  category: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><circle cx="12" cy="12" r="9"/><path d="M12 3v9l6 3"/></svg>,
  discipline: (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" {...p}><path d="M12 2v20M4 12h16"/><circle cx="12" cy="12" r="9"/></svg>,
};

const orgTypeLabels = {
  federation: 'اتحاد', national_team: 'منتخب وطني', shooting_club: 'نادي رماية',
  individual: 'فردي', media: 'إعلام', sponsor: 'راعٍ', government: 'جهة حكومية', other: 'أخرى'
};
const categoryLabelType = { rifle: 'بندقية', pistol: 'مسدس', shotgun: 'تراب/سكيت' };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const EventsIcon = Icon.events;
  const ResultsIcon = Icon.results;
  const OrgIcon = Icon.org;
  const CategoryIcon = Icon.category;
  const DisciplineIcon = Icon.discipline;

  useEffect(() => {
    client.get('/dashboard/summary').then(res => setSummary(res.data)).catch(() => {});
  }, []);

  const portalLinks = [
    { to: '/events', label: 'الفعاليات والتسجيل', desc: 'إدارة الفعاليات وتسجيل المشاركين فيها', icon: 'events' },
    { to: '/results', label: 'نتائج الرماية', desc: '12 مسابقة أولمبية — تسجيل ومتابعة النتائج', icon: 'results' },
    { to: '/organizations', label: 'الجهات والاتحادات', desc: 'إدارة الجهات المشاركة', icon: 'org', roles: ['admin','reviewer'] },
    { to: '/categories', label: 'فئات الاعتماد', desc: 'أنواع البطاقات وألوانها', icon: 'category', roles: ['admin','reviewer'] },
    { to: '/disciplines', label: 'مسابقات الرماية', desc: 'إدارة المسابقات الأولمبية الـ12', icon: 'discipline', roles: ['admin','reviewer'] },
  ].filter(l => !l.roles || l.roles.includes(user.role));

  return (
    <div>
      <h3 style={{ margin: '0 0 12px' }}>بوابة النظام</h3>
      <div className="grid grid-2" style={{ marginBottom: 28 }}>
        {portalLinks.map(l => {
          const Ico = Icon[l.icon];
          return (
            <div key={l.to} className="card" style={{ cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'center' }} onClick={() => navigate(l.to)}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--signal-tint)', color: 'var(--signal-dark)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Ico width="22" height="22" />
              </div>
              <div>
                <div style={{ fontWeight: 700 }}>{l.label}</div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{l.desc}</div>
              </div>
            </div>
          );
        })}
      </div>

      <h3 style={{ margin: '0 0 12px' }}>الإحصائيات العامة</h3>
      {!summary ? (
        <div className="card">جارٍ التحميل...</div>
      ) : (
        <div className="panel-hub">
          {/* Events & Registration */}
          <div className="panel" style={{ '--panel-accent': '#0b8793' }}>
            <div className="panel-head">
              <div className="panel-icon"><EventsIcon width="20" height="20" /></div>
              <div className="panel-title">الفعاليات والتسجيل</div>
            </div>
            <div className="panel-kpis">
              <div><div className="panel-kpi-num">{summary.events.count}</div><div className="panel-kpi-label">فعالية</div></div>
              <div><div className="panel-kpi-num">{summary.events.openEventsCount}</div><div className="panel-kpi-label">مفتوحة للتسجيل</div></div>
              <div><div className="panel-kpi-num">{summary.events.totalParticipants}</div><div className="panel-kpi-label">إجمالي المشاركين</div></div>
              <div><div className="panel-kpi-num">{summary.events.avgPerEvent}</div><div className="panel-kpi-label">متوسط لكل فعالية</div></div>
              <div><div className="panel-kpi-num">{summary.events.male}</div><div className="panel-kpi-label">ذكور</div></div>
              <div><div className="panel-kpi-num">{summary.events.female}</div><div className="panel-kpi-label">إناث</div></div>
              <div><div className="panel-kpi-num">{summary.events.countriesCount}</div><div className="panel-kpi-label">جنسية ممثّلة</div></div>
            </div>
            {summary.events.topEvent && (
              <div className="panel-highlight">
                <div>الأعلى تسجيلاً:</div>
                <div><b>{summary.events.topEvent.name_ar}</b> ({summary.events.topEvent.count})</div>
              </div>
            )}
          </div>

          {/* Results */}
          <div className="panel" style={{ '--panel-accent': '#c9a227' }}>
            <div className="panel-head">
              <div className="panel-icon"><ResultsIcon width="20" height="20" /></div>
              <div className="panel-title">نتائج الرماية</div>
            </div>
            <div className="panel-kpis">
              <div><div className="panel-kpi-num">{summary.results.disciplinesCount}</div><div className="panel-kpi-label">مسابقة</div></div>
              <div><div className="panel-kpi-num">{summary.results.totalResults}</div><div className="panel-kpi-label">نتيجة مسجلة</div></div>
              <div><div className="panel-kpi-num">{summary.results.qualificationCount}</div><div className="panel-kpi-label">نتيجة تصفيات</div></div>
              <div><div className="panel-kpi-num">{summary.results.finalCount}</div><div className="panel-kpi-label">نتيجة نهائي</div></div>
              <div><div className="panel-kpi-num">{summary.results.distinctShooters}</div><div className="panel-kpi-label">رامٍ لديه نتائج</div></div>
              <div><div className="panel-kpi-num">{summary.results.recordsCount}</div><div className="panel-kpi-label">رقم قياسي 🏅</div></div>
            </div>
            {summary.results.mostCompetitive && (
              <div className="panel-highlight">
                <div>الأكثر تنافسًا:</div>
                <div><b>{summary.results.mostCompetitive.name_ar}</b> ({summary.results.mostCompetitive.count} رامٍ)</div>
              </div>
            )}
          </div>

          {/* Organizations */}
          <div className="panel" style={{ '--panel-accent': '#2563eb' }}>
            <div className="panel-head">
              <div className="panel-icon"><OrgIcon width="20" height="20" /></div>
              <div className="panel-title">الجهات والاتحادات</div>
            </div>
            <div className="panel-kpis">
              <div><div className="panel-kpi-num">{summary.organizations.count}</div><div className="panel-kpi-label">جهة مسجلة</div></div>
              <div><div className="panel-kpi-num">{summary.organizations.activeOrgsCount}</div><div className="panel-kpi-label">جهة نشطة</div></div>
              <div><div className="panel-kpi-num">{summary.organizations.countriesCount}</div><div className="panel-kpi-label">دولة</div></div>
            </div>
            <div className="panel-breakdown">
              {summary.organizations.byType.map((t, i) => {
                const max = Math.max(...summary.organizations.byType.map(x => x.count), 1);
                return (
                  <div className="panel-breakdown-row" key={i}>
                    <span className="panel-breakdown-label">{orgTypeLabels[t.type] || t.type}</span>
                    <div className="panel-breakdown-bar"><div className="panel-breakdown-fill" style={{ width: `${(t.count / max) * 100}%`, background: '#2563eb' }} /></div>
                    <span className="panel-breakdown-count">{t.count}</span>
                  </div>
                );
              })}
            </div>
            {summary.organizations.topOrg && (
              <div className="panel-highlight">
                <div>الأعلى تسجيلاً:</div>
                <div><b>{summary.organizations.topOrg.name_ar}</b> ({summary.organizations.topOrg.count})</div>
              </div>
            )}
          </div>

          {/* Categories */}
          <div className="panel" style={{ '--panel-accent': '#7c3aed' }}>
            <div className="panel-head">
              <div className="panel-icon"><CategoryIcon width="20" height="20" /></div>
              <div className="panel-title">فئات الاعتماد</div>
            </div>
            <div className="panel-kpis">
              <div><div className="panel-kpi-num">{summary.categories.count}</div><div className="panel-kpi-label">فئة اعتماد</div></div>
              <div><div className="panel-kpi-num">{summary.categories.breakdown.reduce((s, c) => s + c.count, 0)}</div><div className="panel-kpi-label">مشارك مصنّف</div></div>
            </div>
            <div className="panel-breakdown">
              {summary.categories.breakdown.map((c, i) => {
                const max = Math.max(...summary.categories.breakdown.map(x => x.count), 1);
                return (
                  <div className="panel-breakdown-row" key={i}>
                    <span className="panel-breakdown-label">{c.name_ar}</span>
                    <div className="panel-breakdown-bar"><div className="panel-breakdown-fill" style={{ width: `${(c.count / max) * 100}%`, background: c.color_code || '#7c3aed' }} /></div>
                    <span className="panel-breakdown-count">{c.count}</span>
                  </div>
                );
              })}
            </div>
            {summary.categories.breakdown.length > 0 && (
              <div className="panel-highlight">
                <div>الأعلى: <b>{summary.categories.breakdown[0].name_ar}</b></div>
                <div>الأقل: <b>{summary.categories.breakdown[summary.categories.breakdown.length - 1].name_ar}</b></div>
              </div>
            )}
          </div>

          {/* Disciplines catalogue */}
          <div className="panel" style={{ '--panel-accent': '#dc2626' }}>
            <div className="panel-head">
              <div className="panel-icon"><DisciplineIcon width="20" height="20" /></div>
              <div className="panel-title">مسابقات الرماية</div>
            </div>
            <div className="panel-kpis">
              <div><div className="panel-kpi-num">{summary.disciplines.count}</div><div className="panel-kpi-label">مسابقة أولمبية</div></div>
              <div><div className="panel-kpi-num">{summary.disciplines.totalFinalistSlots}</div><div className="panel-kpi-label">مقعد نهائي إجمالي</div></div>
              <div><div className="panel-kpi-num">{summary.disciplines.count ? Math.round(summary.results.distinctShooters / summary.disciplines.count) : 0}</div><div className="panel-kpi-label">متوسط رماة/مسابقة</div></div>
              {summary.disciplines.byGender.map((g, i) => (
                <div key={i}><div className="panel-kpi-num">{g.count}</div><div className="panel-kpi-label">{g.gender === 'male' ? 'مسابقات رجال' : 'مسابقات سيدات'}</div></div>
              ))}
            </div>
            <div className="panel-breakdown">
              {summary.disciplines.byCategory.map((c, i) => {
                const max = Math.max(...summary.disciplines.byCategory.map(x => x.count), 1);
                return (
                  <div className="panel-breakdown-row" key={i}>
                    <span className="panel-breakdown-label">{categoryLabelType[c.category] || c.category}</span>
                    <div className="panel-breakdown-bar"><div className="panel-breakdown-fill" style={{ width: `${(c.count / max) * 100}%`, background: '#dc2626' }} /></div>
                    <span className="panel-breakdown-count">{c.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
